import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FloralDivider, CornerFlourish, Flower } from "@/components/Ornaments";

const EASE = [0.16, 1, 0.3, 1];

/*
 * A formal envelope that guests "unwrap" to enter the invitation.
 * Choreography: tap → the seal melts and the flap folds back → the card
 * rises out of the pocket → the card itself expands until it fills the
 * screen → the cream card crossfades into the page, whose hero is
 * already rising underneath. The opening tap doubles as the user
 * gesture browsers require before music may play.
 */
export default function EnvelopeGate({
  onOpen,
  onReveal,
  kicker = "Sagai · 3 Nov 2026",
  names = ["Divyanshi", "Divya Shiv"],
  hint = "Tap to open your invitation",
}) {
  const [opening, setOpening] = useState(false);
  const [expanding, setExpanding] = useState(false);
  const [gone, setGone] = useState(false);
  const cardRef = useRef(null);

  const open = () => {
    if (opening) return;
    setOpening(true);
    onOpen?.(); // start the music on the guest's tap

    // Phase 2: the card grows until it covers the viewport.
    setTimeout(() => {
      const el = cardRef.current;
      if (el) {
        const r = el.getBoundingClientRect();
        const scale =
          Math.max(
            window.innerWidth / Math.max(r.width, 1),
            window.innerHeight / Math.max(r.height, 1),
          ) * 1.25;
        el.style.setProperty("--card-scale", String(scale));
      }
      setExpanding(true);
    }, 1250);

    // Phase 3: the page starts revealing beneath the cream card…
    setTimeout(() => onReveal?.(), 2050);
    // …and the gate (card included) dissolves into it.
    setTimeout(() => setGone(true), 2450);
  };

  return (
    <AnimatePresence>
      {!gone && (
        <motion.div
          className={`env-gate${expanding ? " expanding" : ""}`}
          data-testid="envelope-gate"
          role="button"
          tabIndex={0}
          aria-label="Open the invitation"
          onClick={open}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") open();
          }}
          exit={{ opacity: 0, transition: { duration: 1.1, ease: "easeInOut" } }}
        >
          <CornerFlourish size={64} className="env-flourish ef-tl" />
          <CornerFlourish size={64} className="env-flourish ef-tr" />
          <CornerFlourish size={64} className="env-flourish ef-bl" />
          <CornerFlourish size={64} className="env-flourish ef-br" />

          <motion.div
            className={`envelope${opening ? " opening" : ""}${
              expanding ? " expanding" : ""
            }`}
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.2 }}
          >
            {/* card tucked inside — rises out, then expands into the page */}
            <div className="env-card" ref={cardRef}>
              <div className="env-card-content">
                <span className="env-card-kicker">{kicker}</span>
                <span className="env-card-names serif">
                  {names[0]} <i>&amp;</i> {names[1]}
                </span>
                <span className="env-card-divider">
                  <FloralDivider width={120} />
                </span>
              </div>
            </div>

            {/* envelope pocket */}
            <div className="env-body">
              <span className="env-side-l" aria-hidden="true" />
              <span className="env-side-r" aria-hidden="true" />
              <span className="env-bottom" aria-hidden="true" />
            </div>

            {/* flap + wax seal */}
            <div className="env-flap" aria-hidden="true" />
            <div className="env-seal serif" aria-hidden="true">
              <span className="env-seal-letter">D</span>
              <svg
                className="env-seal-flower"
                viewBox="0 0 24 24"
                width="11"
                fill="none"
              >
                <Flower x={12} y={12} s={0.7} fill="#dcc9a0" center="#c3a572" />
              </svg>
              <span className="env-seal-letter">D</span>
            </div>
          </motion.div>

          <motion.div
            className="env-hint kicker"
            initial={{ opacity: 0 }}
            animate={{ opacity: opening ? 0 : 1 }}
            transition={{ duration: 0.6, delay: opening ? 0 : 1 }}
          >
            {hint}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
